import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, type ReactNode, useCallback, useEffect, useRef } from "react";
import { FONT_SIZE, INK, MOTION, NEUTRAL } from "../../../theme/tokens";

/** One insertable placeholder: the literal `token` that lands in the text, plus the chip's visible `label`. */
export type InsertableVariable = {
	/** The literal string written into the value, e.g. `{first_name}`. */
	token: string;
	/** Already-translated chip label — the caller owns i18n. */
	label: string;
};

type Props = {
	/** The message body. Plain text, tokens included verbatim — the caller never has to parse markup back out. */
	value: string;
	onChange: (value: string) => void;
	/** The chips offered above the field. An empty list hides the whole insert row. */
	variables: InsertableVariable[];
	/** Already-translated lead-in beside the chips (e.g. "Insert"). Omit to show chips alone. */
	insertLabel?: string;
	/** Already-translated accessible name for the editable region — it has no visible label of its own. */
	ariaLabel: string;
	placeholder?: string;
	maxLength?: number;
	/** Rendered under the field, in the field's own column — a locked line, a counter, a warning. */
	footer?: ReactNode;
	disabled?: boolean;
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * A message body that can have named placeholders dropped into it at the caret.
 *
 * Deliberately a plain `<textarea>` under a chip row rather than a `contenteditable` with atomic token
 * spans: the value this produces is the exact string that gets sent, so nothing has to serialize rich
 * markup back to text — and a textarea keeps native selection, undo, IME and screen-reader behaviour
 * that a hand-rolled editable region loses. Tokens read as literal `{name}` text, which is also how
 * they read in the message that arrives.
 *
 * Generic over any templated-message surface (a welcome re-send, a broadcast, a notification template):
 * it knows nothing about what a variable *means*, only where the caret is.
 */
export const VariableInsertField: FC<Props> = ({
	value,
	onChange,
	variables,
	insertLabel,
	ariaLabel,
	placeholder,
	maxLength,
	footer,
	disabled,
	sx,
	dataTestId,
}) => {
	const inputRef = useRef<HTMLTextAreaElement>(null);
	// The caret as of the last time the field had it. A chip click blurs the field *before* its handler
	// runs, so reading selectionStart there would report the end of the text — which is why an insert
	// used to land at the end no matter where the user was typing.
	const caretRef = useRef<number | null>(null);

	const rememberCaret = useCallback(() => {
		const el = inputRef.current;
		if (el) caretRef.current = el.selectionStart;
	}, []);

	// Grow to the content so the default body is fully visible without an inner scrollbar.
	useEffect(() => {
		const el = inputRef.current;
		if (!el) return;
		el.style.height = "auto";
		el.style.height = `${el.scrollHeight}px`;
	}, []);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>) => {
			const el = event.currentTarget;
			el.style.height = "auto";
			el.style.height = `${el.scrollHeight}px`;
			caretRef.current = el.selectionStart;
			onChange(el.value);
		},
		[onChange],
	);

	const insert = useCallback(
		(token: string) => {
			const el = inputRef.current;
			// No caret yet (the user clicked a chip before ever focusing) — append, which is what the
			// user can see happening, rather than silently writing to position 0.
			const at = caretRef.current ?? value.length;
			const next = `${value.slice(0, at)}${token}${value.slice(at)}`;
			if (maxLength !== undefined && next.length > maxLength) return;
			const caretAfter = at + token.length;
			caretRef.current = caretAfter;
			onChange(next);
			// Put the caret back where the token ended, after React has written the new value.
			requestAnimationFrame(() => {
				if (!el) return;
				el.focus();
				el.setSelectionRange(caretAfter, caretAfter);
			});
		},
		[value, onChange, maxLength],
	);

	return (
		<Box sx={sx} data-testid={dataTestId}>
			{variables.length > 0 && (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
					{insertLabel && <Typography sx={{ fontSize: FONT_SIZE.LABEL_SM, color: INK.MUTED }}>{insertLabel}</Typography>}
					{variables.map((variable) => (
						<Box
							key={variable.token}
							component="button"
							type="button"
							disabled={disabled}
							data-testid={dataTestId ? `${dataTestId}-insert-${variable.label}` : undefined}
							onClick={() => insert(variable.token)}
							sx={(theme) => ({
								border: 0,
								borderRadius: 1,
								px: 1,
								py: 0.375,
								cursor: disabled ? "not-allowed" : "pointer",
								fontFamily: "inherit",
								fontSize: FONT_SIZE.LABEL_SM,
								color: theme.palette.primary.dark,
								backgroundColor: alpha(theme.palette.primary.main, 0.08),
								transition: `background-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
								"@media (hover: hover) and (pointer: fine)": {
									"&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
								},
								"&:active": { transform: "scale(0.97)" },
								"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active": { transform: "none" } },
							})}
						>
							{variable.label}
						</Box>
					))}
				</Box>
			)}
			<Box
				component="textarea"
				ref={inputRef}
				aria-label={ariaLabel}
				placeholder={placeholder}
				maxLength={maxLength}
				disabled={disabled}
				value={value}
				onChange={handleChange}
				onSelect={rememberCaret}
				onKeyUp={rememberCaret}
				onBlur={rememberCaret}
				data-testid={dataTestId ? `${dataTestId}-input` : undefined}
				sx={(theme) => ({
					mt: 1.25,
					width: "100%",
					display: "block",
					resize: "none",
					overflow: "hidden",
					border: 0,
					borderBottom: `1px solid ${NEUTRAL[300]}`,
					borderRadius: 0,
					backgroundColor: "transparent",
					padding: "2px 0 11px",
					fontFamily: "inherit",
					fontSize: "14px",
					lineHeight: 1.6,
					color: INK.PRIMARY,
					transition: `border-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
					"&:focus": { outline: "none", borderBottomColor: theme.palette.primary.main },
					"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				})}
			/>
			{footer}
		</Box>
	);
};

export default VariableInsertField;
